// @ts-strict-ignore
import { NumberUtils } from '@shared/util/number-utils';
import { PropertyValueType } from '@project/constants/property-value-type';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { FloatRangeVO, IntRangeVO, LocalizedPropertyValueVO, LocalizedPropertyValueWithUnitVO, PropertyInLocalesVO, PropertyValueVO, PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectType } from '@project/model/project-type';
import { MetaElementFeaturePropertyDef } from '@project/constants/meta-element-feature-property-def';
import { ArticleConnectionPropertyDef } from '../constants/article-connection-property-def';
import { IsProjectType } from '@project/model/is-project-type';

export class PropertyUtils {

  public static readonly INTERNAL_CONNECTION_PROPERTY_NAMES: string[] = [MetaElementBehaviorPropertyDef.MULTIPURPOSE_CONNECTION_SET_ID];

  public static propertiesShareCommonPropertyValue(propertyA: PropertyVO | undefined, propertyB: PropertyVO | undefined): boolean {
    if (!propertyA || !propertyB || ArrayUtils.isEmpty(propertyA.values) || ArrayUtils.isEmpty(propertyB.values)) {
      return false;
    }
    for (const propertyValueA of propertyA.values) {
      for (const propertyValueB of propertyB.values) {
        if (propertyValueA.equals(propertyValueB)) {
          return true;
        }
      }
    }
    return false;
  }

  public static sourcePropertiesContainTargetPropertyNames(sourceProperties: PropertyVO[], targetPropertyNames: string[]): boolean {
    if (ArrayUtils.isEmpty(targetPropertyNames)) {
      return true;
    }
    if (ArrayUtils.isEmpty(sourceProperties)) {
      return false;
    }
    for (const targetPropertyName of targetPropertyNames) {
      const property: PropertyVO = this.getPropertyByName(sourceProperties, targetPropertyName);
      if (!property) {
        return false;
      }
    }
    return true;
  }

  public static removePropertiesByName(properties: PropertyVO[], propertyName: string): void {
    if (!propertyName) {
      return;
    }
    PropertyUtils.removePropertiesByNames(properties, [propertyName]);
  }

  public static removePropertiesByNames(properties: PropertyVO[], propertyNames: string[]): void {
    if (!properties || properties.length === 0 || !propertyNames || propertyNames.length === 0) {
      return;
    }
    for (let i = 0; i < properties.length; i++) {
      const property: PropertyVO = properties[i] as PropertyVO;
      if (property && propertyNames.indexOf(property.name) !== -1) {
        properties.splice(i, 1);
        i--;
      }
    }
  }

  public static containsProperty(properties: PropertyVO[], propertyName: string): boolean {
    return !!PropertyUtils.getPropertyByName(properties, propertyName);
  }

  public static setStringPropertyValue(properties: PropertyVO[], propertyName: string, stringValue: string,
                                       overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    const propertyValue: PropertyValueVO = PropertyUtils.createStringPropertyValue(stringValue);
    PropertyUtils.setSinglePropertyValue(properties, propertyName, propertyValue, overwriteExistingValues, isTransientProperty);
  }

  public static setStringPropertyValues(properties: PropertyVO[], propertyName: string, stringValues: string[],
                                        overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    const propertyValues: PropertyValueVO[] = [];
    for (const stringValue of stringValues) {
      propertyValues.push(PropertyUtils.createStringPropertyValue(stringValue));
    }
    PropertyUtils.setPropertyValues(properties, propertyName, propertyValues, overwriteExistingValues, false, isTransientProperty);
  }

  public static pushStringPropertyValue(properties: PropertyVO[], propertyName: string, stringValue: string): void {
    const propertyValue: PropertyValueVO = PropertyUtils.createStringPropertyValue(stringValue);
    PropertyUtils.setPropertyValues(properties, propertyName, [propertyValue], false, true);
  }

  public static createStringPropertyValue(stringValue: string): PropertyValueVO {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.STRING;
    propertyValue.stringValue = stringValue;
    return propertyValue;
  }

  public static getStringPropertyValue(properties: PropertyVO[], propertyName: string): string {
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(properties, propertyName);
    if (!propertyValue) {
      return null;
    }
    return propertyValue.stringValue;
  }

  public static getStringPropertyValueByProperty(property: PropertyVO): string {
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValue(property);
    if (!propertyValue) {
      return null;
    }
    return propertyValue.stringValue;
  }

  public static setFloatPropertyValue(properties: PropertyVO[], propertyName: string, floatValue: number,
                                      overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    const propertyValue: PropertyValueVO = PropertyUtils.createFloatPropertyValue(floatValue);
    PropertyUtils.setSinglePropertyValue(properties, propertyName, propertyValue, overwriteExistingValues, isTransientProperty);
  }

  public static createFloatPropertyValue(floatValue: number): PropertyValueVO {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.FLOAT;
    propertyValue.floatValue = floatValue;
    return propertyValue;
  }

  public static getLocalizedPropertyValueAsStringByLocaleChain(property: PropertyInLocalesVO, localeChain: string[]): string {
    if (!property) {
      return ``;
    }
    if (!localeChain || !localeChain.length) {
      return PropertyUtils.getLocalizedPropertyValueAsString(property, null);
    } else {
      const result = localeChain
        .map((locale: string) => PropertyUtils.getLocalizedPropertyValueAsString(property, locale))
        .find((label: string) => !StringUtils.isEmpty(label));
      return result || ``;
    }
  }

  public static getLocalizedPropertyValueAsString(property: PropertyInLocalesVO, locale: string): string {
    let label: string = ``;
    const propertyValues: LocalizedPropertyValueVO[] = property ? property.values : [];
    for (const propertyValue of propertyValues) {
      if (locale === propertyValue.locale || !propertyValue.locale) {
        if (label.length) {
          label += ` | `;
        }
        label += propertyValue.toString();
        if (propertyValue instanceof LocalizedPropertyValueWithUnitVO &&
          !StringUtils.isEmpty((<LocalizedPropertyValueWithUnitVO>propertyValue).unit)) {
          label += ` ${(<LocalizedPropertyValueWithUnitVO>propertyValue).unit.trim()}`;
        }
      }
    }
    return label;
  }

  public static setFloatRangePropertyValue(properties: PropertyVO[], propertyName: string, leftValue: number,
                                           rightValue: number): void {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.FLOATRANGE;
    propertyValue.floatRange = new FloatRangeVO();
    propertyValue.floatRange.left = leftValue;
    propertyValue.floatRange.right = rightValue;
    PropertyUtils.setSinglePropertyValue(properties, propertyName, propertyValue);
  }

  public static getFloatPropertyValue(properties: PropertyVO[], propertyName: string): number {
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(properties, propertyName);
    if (!propertyValue) {
      return NaN;
    }
    return propertyValue.floatValue;
  }

  public static getFloatRangePropertyValue(properties: PropertyVO[], propertyName: string): FloatRangeVO {
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(properties, propertyName);
    if (!propertyValue) {
      return null;
    }
    return propertyValue.floatRange;
  }

  public static setIntPropertyValue(properties: PropertyVO[], propertyName: string, intValue: number,
                                    overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    const propertyValue: PropertyValueVO = PropertyUtils.createIntPropertyValue(intValue);
    PropertyUtils.setSinglePropertyValue(properties, propertyName, propertyValue, overwriteExistingValues, isTransientProperty);
  }

  public static setIntPropertyValues(properties: PropertyVO[], propertyName: string, intValues: number[],
                                        overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    const propertyValues: PropertyValueVO[] = [];
    for (const intValue of intValues) {
      propertyValues.push(PropertyUtils.createIntPropertyValue(intValue));
    }
    PropertyUtils.setPropertyValues(properties, propertyName, propertyValues, overwriteExistingValues, false, isTransientProperty);
  }

  public static pushIntPropertyValue(properties: PropertyVO[], propertyName: string, intValue: number): void {
    const propertyValue: PropertyValueVO = PropertyUtils.createIntPropertyValue(intValue);
    PropertyUtils.setPropertyValues(properties, propertyName, [propertyValue], false, true);
  }

  public static createIntPropertyValue(intValue: number): PropertyValueVO {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.INT;
    propertyValue.intValue = NumberUtils.toInt32(intValue);
    return propertyValue;
  }

  public static getIntPropertyValue(properties: PropertyVO[], propertyName: string): number {
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(properties, propertyName);
    if (!propertyValue) {
      return NaN;
    }
    return propertyValue.intValue;
  }

  public static getIntPropertyValueByProperty(property: PropertyVO): number {
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValue(property);
    if (!propertyValue) {
      return NaN;
    }
    return propertyValue.intValue;
  }

  public static getNumberPropertyValue(properties: PropertyVO[], propertyName: string, defaultValue: number = NaN): number {
    if (!properties || !propertyName) {
      return defaultValue;
    }
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(properties, propertyName);
    if (!propertyValue) {
      return defaultValue;
    }
    if (PropertyValueType.INT === propertyValue.type) {
      return PropertyUtils.getIntPropertyValue(properties, propertyName);
    }
    if (PropertyValueType.FLOAT === propertyValue.type) {
      return PropertyUtils.getFloatPropertyValue(properties, propertyName);
    }
    return defaultValue;
  }

  public static getBooleanPropertyValue(properties: PropertyVO[], propertyName: string, defaultValue: boolean = true): boolean {
    let property = PropertyUtils.getPropertyByName(properties, propertyName);
    if(!property) {
      return false;
    }

    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValue(property);
    if (!propertyValue) {
      return defaultValue;
    }
    return propertyValue.intValue !== 0;
  }

  public static setBooleanPropertyValue(properties: PropertyVO[], propertyName: string, booleanValue: boolean,
                                        overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    const propertyValue: PropertyValueVO = PropertyUtils.createBooleanPropertyValue(booleanValue);
    PropertyUtils.setSinglePropertyValue(properties, propertyName, propertyValue, overwriteExistingValues, isTransientProperty);
  }

  public static pushBooleanPropertyValue(properties: PropertyVO[], propertyName: string, booleanValue: boolean): void {
    const propertyValue: PropertyValueVO = PropertyUtils.createBooleanPropertyValue(booleanValue);
    PropertyUtils.setPropertyValues(properties, propertyName, [propertyValue], false, true);
  }

  public static createBooleanPropertyValue(booleanValue: boolean): PropertyValueVO {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.INT;
    propertyValue.intValue = Number(booleanValue);
    return propertyValue;
  }

  public static getIntRangePropertyValue(properties: PropertyVO[], propertyName: string): IntRangeVO {
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(properties, propertyName);
    if (!propertyValue) {
      return null;
    }
    return propertyValue.intRange;
  }

  public static setIntRangePropertyValue(properties: PropertyVO[], propertyName: string, intRangeValue: IntRangeVO,
                                         overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.INTRANGE;
    propertyValue.intRange = intRangeValue;
    PropertyUtils.setSinglePropertyValue(properties, propertyName, propertyValue, overwriteExistingValues, isTransientProperty);
  }

  public static getIntPropertyValues(properties: PropertyVO[], propertyName: string): number[] {
    const intValues: number[] = [];
    const propertyValues: PropertyValueVO[] = PropertyUtils.getPropertyValuesByPropertyName(properties, propertyName);
    for (const value of propertyValues) {
      if (value.type === PropertyValueType.INT) {
        intValues.push(value.intValue);
      }
    }
    return intValues;
  }

  public static getFloatPropertyValues(properties: PropertyVO[], propertyName: string): number[] {
    const floatValues: number[] = [];
    const propertyValues: PropertyValueVO[] = PropertyUtils.getPropertyValuesByPropertyName(properties, propertyName);
    for (const value of propertyValues) {
      if (value.type === PropertyValueType.FLOAT) {
        floatValues.push(value.floatValue);
      }
    }
    return floatValues;
  }

  public static getStringPropertyValues(properties: PropertyVO[], propertyName: string): string[] {
    const stringValues: string[] = [];
    const propertyValues: PropertyValueVO[] = PropertyUtils.getPropertyValuesByPropertyName(properties, propertyName);
    for (const value of propertyValues) {
      if (value.type === PropertyValueType.STRING) {
        stringValues.push(value.stringValue);
      }
    }
    return stringValues;
  }

  public static getBooleanPropertyValues(properties: PropertyVO[], propertyName: string): boolean[] {
    const booleanValues: boolean[] = [];
    const propertyValues: PropertyValueVO[] = PropertyUtils.getPropertyValuesByPropertyName(properties, propertyName);
    for (const value of propertyValues) {
      if (value.type === PropertyValueType.INT) {
        booleanValues.push(value.intValue !== 0);
      }
    }
    return booleanValues;
  }

  public static shiftStringPropertyValue(properties: PropertyVO[], propertyName: string): string {
    const shiftedValue: PropertyValueVO = this.shiftPropertyValueByPropertyName(properties, propertyName);
    return shiftedValue ? shiftedValue.stringValue : null;
  }

  public static shiftIntPropertyValue(properties: PropertyVO[], propertyName: string): number {
    const shiftedValue: PropertyValueVO = this.shiftPropertyValueByPropertyName(properties, propertyName);
    return shiftedValue ? shiftedValue.intValue : NaN;
  }

  /**
   * Removes first property-value from property with the given property-name found in the given list of properties.
   * NOTE: property will be removed from source-list in case it won't contain property-value after removal of the first property-value.
   *
   * @param properties source list of properties
   * @param propertyName name of the property first value from which should be shifted
   * @return removed (shifted) property value, null if no value was shifted
   */
  public static shiftPropertyValueByPropertyName(properties: PropertyVO[], propertyName: string): PropertyValueVO {
    if (ArrayUtils.isEmpty(properties) || !propertyName) {
      return null;
    }

    const property: PropertyVO = this.getPropertyByName(properties, propertyName);
    const shiftedValue: PropertyValueVO = this.shiftPropertyValue(property);
    if (shiftedValue) {
      // remove property if it doesn't contain values anymore
      if (ArrayUtils.isEmpty(property.values)) {
        properties.splice(properties.indexOf(property), 1);
      }
    }
    return shiftedValue;
  }

  /**
   * Removes first property-value from the given property and returns that property-value.
   */
  public static shiftPropertyValue(property: PropertyVO): PropertyValueVO {
    if (!property || ArrayUtils.isEmpty(property.values)) {
      return null;
    }
    return property.values.shift();
  }

  public static replaceStringPropertyValue(properties: PropertyVO[], propertyName: string, propertyValueFrom: string,
                                           propertyValueTo: string): boolean {
    if (!propertyValueFrom || !propertyValueTo) {
      return false;
    }
    const property: PropertyVO = PropertyUtils.getPropertyByName(properties, propertyName);
    return PropertyUtils.replaceStringValueInProperty(property, propertyValueFrom, propertyValueTo);
  }

  public static replaceStringValueInProperty(property: PropertyVO, propertyValueFrom: string, propertyValueTo: string): boolean {
    if (!property || !property.values || property.values.length === 0) {
      return false;
    }
    for (const propertyValue of property.values) {
      if (propertyValue.type === PropertyValueType.STRING && propertyValue.stringValue === propertyValueFrom) {
        propertyValue.stringValue = propertyValueTo;
        return true;
      }
    }
    return false;
  }

  public static getFirstPropertyValueByPropertyName(properties: PropertyVO[], propertyName: string): PropertyValueVO {
    return PropertyUtils.getFirstPropertyValue(PropertyUtils.getPropertyByName(properties, propertyName));
  }

  public static getFirstPropertyValue(property: PropertyVO): PropertyValueVO {
    if (!property || !property.values || property.values.length === 0) {
      return null;
    }
    return property.values[0];
  }

  public static propertyContainsValueAsPropertyValue(property: PropertyVO, value: any): boolean {
    if (!property || !property.values || property.values.length === 0) {
      return false;
    }
    for (const propertyValue of property.values) {
      if (PropertyUtils.propertyValueIsEqualToValue(propertyValue, value)) {
        return true;
      }
    }
    return false;
  }

  public static propertyValueIsEqualToValue(propertyValue: PropertyValueVO, value: any): boolean {
    if (!propertyValue) {
      return false;
    }
    if (propertyValue.type === PropertyValueType.FLOAT) {
      return propertyValue.floatValue === value;
    } else if (propertyValue.type === PropertyValueType.FLOATRANGE) {
      return propertyValue.floatRange === value;
    } else if (propertyValue.type === PropertyValueType.INT) {
      if (typeof value === 'boolean') {
        value = value ? 1 : 0;
      }
      return propertyValue.intValue === value;
    } else if (propertyValue.type === PropertyValueType.INTRANGE) {
      return propertyValue.intRange === value;
    } else if (propertyValue.type === PropertyValueType.STRING) {
      return propertyValue.stringValue === value;
    }
    return false;
  }

  public static getPropertyValuesByPropertyName(properties: PropertyVO[], propertyName: string): PropertyValueVO[] {
    return PropertyUtils.getPropertyValues(PropertyUtils.getPropertyByName(properties, propertyName));
  }

  public static getPropertyValues(property: PropertyVO): PropertyValueVO[] {
    if (!property || !property.values || property.values.length === 0) {
      return [];
    }
    return property.values;
  }

  public static setSinglePropertyValue(properties: PropertyVO[], propertyName: string, propertyValue: PropertyValueVO,
                                       overwriteExistingValues: boolean = true, isTransientProperty: boolean = false): void {
    PropertyUtils.setPropertyValues(properties, propertyName, !propertyValue ? null
      : [propertyValue], overwriteExistingValues, false, isTransientProperty);
  }

  public static setPropertyValues(properties: PropertyVO[], propertyName: string, propertyValues: PropertyValueVO[],
                                  overwriteExistingValues: boolean = true, addAsFirstValue: boolean = false,
                                  isTransientProperty: boolean = false): void {
    if (!properties || !propertyName) {
      return;
    }
    let property: PropertyVO = PropertyUtils.getPropertyByName(properties, propertyName);
    if (!property) {
      property = new PropertyVO();
      property.name = propertyName;
      property.transientProperty = isTransientProperty;
      properties.push(property);
    }
    if (overwriteExistingValues || !property.values) {
      property.values = propertyValues;
    } else {
      property.values = (addAsFirstValue) ? propertyValues.concat(property.values) : property.values.concat(propertyValues);
    }
  }

  public static addPropertyValues(properties: PropertyVO[], propertyName: string, propertyValues: PropertyValueVO[]): void {
    PropertyUtils.setPropertyValues(properties, propertyName, propertyValues, false);
  }

  public static setProperty(properties: PropertyVO[], property: PropertyVO): void {
    if (!properties || !property || !property.name) {
      return;
    }
    PropertyUtils.removePropertiesByNames(properties, [property.name]);
    properties.push(property);
  }

  /**
   * Search property in array by given property name.
   * @param properties array of properties
   * @param propertyName property name
   * @return property
   *
   */
  public static getPropertyByName(properties: PropertyVO[], propertyName: string): PropertyVO {
    if (!properties || !propertyName) {
      return null;
    }
    for (let i = 0; i < properties.length; i++) {
      const property: PropertyVO = properties[i] as PropertyVO;
      if (property && property.name === propertyName) {
        return property;
      }
    }
    return null;
  }

  public static getSwitchRotationAngle(connectionAreas: MetaElementVO[]): number {
    let switchAngle: number = NaN;
    for (const connectionArea of connectionAreas) {
      const connectionSwitchAngle = PropertyUtils.getIntPropertyValue(connectionArea.behaviorProperties,
        MetaElementBehaviorPropertyDef.SWITCH_ANGLE);
      if (NumberUtils.isNumber(connectionSwitchAngle)) {
        if (NumberUtils.isNumber(switchAngle) && switchAngle !== connectionSwitchAngle) {
          return NaN;
        } else {
          switchAngle = connectionSwitchAngle;
        }
      }
    }
    return switchAngle;
  }

  public static getDeltaValue(connectionArea: ConnectionAreaVO, direction: string): number {
    let deltaProperty: PropertyVO;
    switch (direction) {
      case CommonConstants.DIRECTION_U:
        deltaProperty = PropertyUtils.getPropertyByName(connectionArea.behaviorProperties, MetaElementBehaviorPropertyDef.DELTA_U);
        break;
      case CommonConstants.DIRECTION_V:
        deltaProperty = PropertyUtils.getPropertyByName(connectionArea.behaviorProperties, MetaElementBehaviorPropertyDef.DELTA_V);
        break;
      case CommonConstants.DIRECTION_N:
        deltaProperty = PropertyUtils.getPropertyByName(connectionArea.behaviorProperties, MetaElementBehaviorPropertyDef.DELTA_N);
        break;
    }

    if (deltaProperty) {
      for (const value of deltaProperty.values) {
        if (value.type === PropertyValueType.INT) {
          return value.intValue;
        }
      }
    }

    return 0;
  }

  public static getDeltaRange(delta: number, direction: string): IntRangeVO {
    const range: IntRangeVO = new IntRangeVO();
    switch (direction) {
      case CommonConstants.DIRECTION_N:
        range.left = 0;
        range.right = delta;
        break;
      default:
        range.left = -delta;
        range.right = delta;
        break;
    }
    return range;
  }

  /**
   * Checks the suitability of the feature properties to the requirement properties.
   * @param featureProperties feature properties which need to be checked
   * @param requirementProperties requirement properties
   * @return true if feature properties (of connection area) are suitable, otherwise false
   *
   */
  public static checkProperties(featureProperties: PropertyVO[], requirementProperties: PropertyVO[]): boolean {
    for (const requirement of requirementProperties) {
      const feature: PropertyVO = PropertyUtils.getPropertyByName(featureProperties, requirement.name);
      if (!feature || !PropertyUtils.checkProperty(feature, requirement)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Checks the suitability of the feature property to the requirement property with the same name.
   * @param property feature property
   * @param parentProperty requirement property
   * @return true if feature is suitable, otherwise false
   *
   */
  public static checkProperty(property: PropertyVO, parentProperty: PropertyVO): boolean {
    for (const parentPropertyValue of parentProperty.values) {
      if (PropertyUtils.checkPropertyValue(property.values, parentPropertyValue)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks whether the array contains a property value that is suitable for the provided requirement property value.
   * @param propertyValues array of property values
   * @param parentPropertyValue requirement property value
   * @return true if one of the properties from the array is suitable, otherwise false
   *
   */
  public static checkPropertyValue(propertyValues: PropertyValueVO[], parentPropertyValue: PropertyValueVO): boolean {
    for (const propertyValue of propertyValues) {
      switch (parentPropertyValue.type) {
        case PropertyValueType.STRING:
          if (propertyValue.type === PropertyValueType.STRING &&
            propertyValue.stringValue === parentPropertyValue.stringValue) {
            return true;
          }
          break;
        case PropertyValueType.INT:
          if (propertyValue.type === PropertyValueType.INT &&
            propertyValue.intValue === parentPropertyValue.intValue) {
            return true;
          } else if (propertyValue.type === PropertyValueType.INTRANGE && propertyValue.intRange) {
            if (propertyValue.intRange.left <= parentPropertyValue.intValue &&
              propertyValue.intRange.right >= parentPropertyValue.intValue) {
              return true;
            }
          }
          break;
        case PropertyValueType.INTRANGE:
          if (propertyValue.type === PropertyValueType.INT &&
            propertyValue.intValue >= parentPropertyValue.intRange.left &&
            propertyValue.intValue <= parentPropertyValue.intRange.right) {
            return true;
          } else if (propertyValue.type === PropertyValueType.INTRANGE && propertyValue.intRange) {
            if (propertyValue.intRange.left <= parentPropertyValue.intRange.right &&
              propertyValue.intRange.right >= parentPropertyValue.intRange.left) {
              return true;
            }
          }
          break;
        case PropertyValueType.FLOAT:
          if (propertyValue.type === PropertyValueType.FLOAT &&
            propertyValue.floatValue === parentPropertyValue.floatValue) {
            return true;
          } else if (propertyValue.type === PropertyValueType.FLOATRANGE && propertyValue.floatRange) {
            if (propertyValue.floatRange.left <= parentPropertyValue.floatValue &&
              propertyValue.floatRange.right >= parentPropertyValue.floatValue) {
              return true;
            }
          }
          break;
        case PropertyValueType.FLOATRANGE:
          if (propertyValue.type === PropertyValueType.FLOAT &&
            propertyValue.floatValue >= parentPropertyValue.floatRange.left &&
            propertyValue.floatValue <= parentPropertyValue.floatRange.right) {
            return true;
          } else if (propertyValue.type === PropertyValueType.FLOATRANGE && propertyValue.floatRange) {
            if (propertyValue.floatRange.left <= parentPropertyValue.floatRange.right &&
              propertyValue.floatRange.right >= parentPropertyValue.floatRange.left) {
              return true;
            }
          }
          break;
      }
    }

    return false;
  }

  public static findArticleTechnicalInfo(articleTechnicalInfoVOs: PropertyInLocalesVO[], propertyName: string): PropertyInLocalesVO {
    if (!propertyName || !articleTechnicalInfoVOs || !articleTechnicalInfoVOs.length) {
      return;
    }
    for (const property of articleTechnicalInfoVOs) {
      if (propertyName === property.name) {
        return property;
      }
    }
    return null;
  }

  public static containsStringPropertyValue(property: PropertyVO, value: string): boolean {
    if (!property || !property.values || property.values.length === 0) {
      return false;
    }
    for (const propertyValue of property.values) {
      if (propertyValue.type === PropertyValueType.STRING && propertyValue.stringValue === value) {
        return true;
      }
    }
    return false;
  }

  public static getRotationAngles(connectionAreas: MetaElementVO[]): number[] {
    let angles: number[] = [];
    for (const connectionArea of connectionAreas) {
      const connectionAngles: number[] = this.getIntPropertyValues(connectionArea.behaviorProperties,
        MetaElementBehaviorPropertyDef.ROTATION_ANGLES);
      if (connectionAngles.length > 0) {
        if (angles.length === 0) {
          angles = connectionAngles;
        } else {
          for (const angle of angles.concat()) {
            if (connectionAngles.indexOf(angle) === -1) {
              angles.splice(angles.indexOf(angle), 1);
            }
          }
        }
      }
    }
    angles.sort();
    return angles;
  }

  public static getDeltaRangeByConnection(connectionArea: ConnectionAreaVO, direction: string): IntRangeVO {
    const delta: number = this.getDeltaValue(connectionArea, direction);
    return this.getDeltaRange(delta, direction);
  }

  public static getPropertiesByNames(properties: PropertyVO[], propertyNames: string[], cloneProperties: boolean = false): PropertyVO[] {
    if (!properties || ArrayUtils.isEmpty(propertyNames)) {
      return null;
    }
    const result: PropertyVO[] = [];
    for (let i: number = 0; i < properties.length; i++) {
      const property: PropertyVO = properties[i] as PropertyVO;
      if (property && propertyNames.indexOf(property.name) !== -1) {
        if (cloneProperties) {
          result.push(property.clone());
        } else {
          result.push(property);
        }
      }
    }
    return result;
  }

  public static isProjectValidatable(projectType: ProjectType | undefined): boolean {
    return !IsProjectType.among( projectType, "XCOM", "GDS", "MCS", "MARKINGSYSTEM")
  }

  public static isInternalConnectionPropertyName(propertyName: string): boolean {
    if (StringUtils.isEmpty(propertyName)) {
      return false;
    }
    return PropertyUtils.INTERNAL_CONNECTION_PROPERTY_NAMES.indexOf(propertyName) >= 0;
  }

  public static createDistanceProperty(distance: number): PropertyVO {
    const distanceProperty: PropertyVO = new PropertyVO();
    distanceProperty.name = MetaElementFeaturePropertyDef.DISTANCE;
    distanceProperty.values = [];

    const distanceIntValue: PropertyValueVO = new PropertyValueVO();
    distanceIntValue.type = PropertyValueType.INT;
    // there is an always a certain unprecision when dealing with measurements taken from 3d-objects in away3d
    // at the moment i think it is best to just provide int-valued distance here, which is also rounded from the float-valued measurement
    // Rounding takes care of unprecision in floating points, problematic example in PNG-245
    distanceIntValue.intValue = Math.round(distance);
    distanceProperty.values.push(distanceIntValue);

    return distanceProperty;
  }

  public static createArticleAlternativeGroupProperty(alternativeGroups: string): PropertyVO {
    const alternativeGroupProperty: PropertyVO = new PropertyVO();
    alternativeGroupProperty.name = ArticleConnectionPropertyDef.ARTICLE_ALTERNATIVE_GROUP;
    alternativeGroupProperty.values = [PropertyValueVO.create(PropertyValueType.STRING, alternativeGroups)];
    return alternativeGroupProperty;
  }
}
